import { useAuthSites } from "@/app/_hooks/Auth/useAuthSites";
import { uiMediumFont } from "@/app/styles/fonts";
import { ChevronRight } from "@mui/icons-material";
import { Avatar, Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import { signOut, useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useState } from "react";
import UserLanguageSwitchMenu from "./UserLanguageSwitchMenu";
import UserSiteSwitchMenu from "./UserSiteSwitchMenu";

const UserMenu = ({ label }) => {
  const t = useTranslations("usermenu");
  const currentLocale = useLocale();
  const router = useRouter();
  const { status, data: session, update } = useSession();
  const userName = session?.user?.name || label || "";
  const userEmail = session?.user?.email || "";
  const sitesQuery = useAuthSites();
  const currentSiteId = session?.user?.siteId;
  const sites = sitesQuery.data ?? [];
  const currentSiteLabel =
    sites.find((site) => site.id === currentSiteId)?.name ?? t("siteSwitch");
  const [anchorEl, setAnchorEl] = useState(null);
  const [siteMenuAnchor, setSiteMenuAnchor] = useState(null);
  const userMenuOpen = Boolean(anchorEl);

  const handleClickUserMenu = (event) => setAnchorEl(event.currentTarget);

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
    setSiteMenuAnchor(null);
  };
  const closeSiteMenu = () => setSiteMenuAnchor(null);
  const openSiteMenu = (event) => setSiteMenuAnchor(event.currentTarget);

  const handleSelectSite = async (siteId) => {
    if (!siteId || siteId === currentSiteId) {
      closeSiteMenu();
      handleCloseUserMenu();
      return;
    }
    await update({ siteId });
    closeSiteMenu();
    handleCloseUserMenu();
    router.refresh();
  };

  if (status === "loading") return null;
  if (status === "unauthenticated") {
    return (
      <Button
        variant="outlined"
        color="secondary"
        onClick={() => router.push(`/${currentLocale}/login`)}
      >
        {t("login")}
      </Button>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {status === "authenticated" && (
        <>
          <Box
            onClick={handleClickUserMenu}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClickUserMenu(e);
              }
            }}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              textAlign: "left",
              gap: 1,
              width: "100%",
              maxWidth: "100%",
              cursor: "pointer",
              borderRadius: 1.2,
              px: 2,
              py: 2,
              color: "#334155",
              backgroundColor: "rgba(99,102,241,0.12)",
              transition: "background-color .18s ease",
              "&:hover": { backgroundColor: "rgba(99,102,241,0.18)" },
            }}
          >
            <Avatar
              src={session.user.image}
              alt="?"
              sx={{ width: 32, height: 32 }}
              slotProps={{ referrerPolicy: "no-referrer" }}
            />
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              {userName ? (
                <Typography
                  variant="body2"
                  fontWeight={700}
                  className={uiMediumFont.className}
                  noWrap
                  sx={{
                    maxWidth: "100%",
                    lineHeight: 1.2,
                    fontSize: "0.75rem",
                    color: "#334155",
                  }}
                >
                  {userName}
                </Typography>
              ) : null}
              {userEmail ? (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    maxWidth: "100%",
                    fontSize: "0.55rem",
                    lineHeight: 1.2,
                    color: "#64748b",
                  }}
                >
                  {userEmail}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={userMenuOpen}
            onClose={handleCloseUserMenu}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{
              sx: {
                width: "fit-content",
                minWidth: anchorEl?.offsetWidth ?? undefined,
                maxWidth: "calc(100vw - 24px)",
              },
            }}
            MenuListProps={{
              "aria-labelledby": "basic-button",
              sx: {
                width: "auto",
                whiteSpace: "nowrap",
                py: 0.25,
                "& .MuiMenuItem-root": {
                  minHeight: 36,
                  py: 1,
                  px: 1,
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                },
              },
            }}
          >
            <MenuItem onClick={openSiteMenu}>
              <div className="flex justify-between w-full items-center">
                <span>{currentSiteLabel}</span>
                <ChevronRight fontSize="small" />
              </div>
            </MenuItem>
            <UserLanguageSwitchMenu
              parentOpen={userMenuOpen}
              onClose={handleCloseUserMenu}
            />
            <MenuItem
              onClick={() => {
                handleCloseUserMenu();
                if (typeof window === "undefined") return;
                const returnTo = `/${currentLocale}/login`;
                const logoutUrl = `/api/auth/logout?returnTo=${encodeURIComponent(returnTo)}`;
                void signOut({ redirect: false }).finally(() => {
                  window.location.assign(logoutUrl);
                });
              }}
            >
              {t("logout")}
            </MenuItem>
          </Menu>

          <UserSiteSwitchMenu
            anchorEl={siteMenuAnchor}
            open={Boolean(siteMenuAnchor)}
            onClose={closeSiteMenu}
            sites={sites}
            currentSiteId={currentSiteId}
            onSelectSite={handleSelectSite}
            isLoading={sitesQuery.isLoading}
          />
        </>
      )}
    </div>
  );
};

export default UserMenu;
